import { createSignal, For, type JSX } from 'solid-js';
import { CardGridSkeleton } from '../components/CardGridSkeleton';
import { DigestCard } from '../components/DigestCard';
import { DigestDrawer } from '../components/DigestDrawer';
import { EmptyState } from '../components/EmptyState';
import { LoadMore } from '../components/LoadMore';
import { PageHeader } from '../components/PageHeader';
import { Panel } from '../components/Panel';
import { QueryBoundary } from '../components/QueryBoundary';
import { t } from '../i18n';
import { type DigestSummary, useDigests } from '../lib/digests';
import { usePagedQuery } from '../lib/paged';
import { SESSION_TOKEN } from '../lib/session';

// Past weekly digests (mock SimplePage '/digests': Panel "History" over a
// .row-list of weeks). The list comes from `GET /v1/digests` — keyset-paged, so
// pages accumulate behind <LoadMore/> and the archive doesn't stop at week one;
// opening a row fetches its stored email (`GET /v1/digests/:id`) into the
// reading drawer (#328). The upcoming digest lives in the Panel footer (muted
// one-liner). `token` is injectable for tests; the router mounts this with no
// props → dev/prod session. No session (null token) → the query stays idle and
// the QueryBoundary shows the honest empty state.

export function Digests(props: { token?: string | null } = {}): JSX.Element {
  // Default to the dev session ONLY when the prop is omitted — an explicit
  // `null` is a signed-out caller (`??` would wrongly replace it).
  const token = props.token === undefined ? SESSION_TOKEN : props.token;
  const digests = usePagedQuery<DigestSummary>((cursor) => useDigests(cursor, token));

  const [selected, setSelected] = createSignal<DigestSummary | null>(null);

  return (
    <section class="page digests">
      <PageHeader title={t('digests.title')} desc={t('digests.subtitle')} />

      <QueryBoundary
        query={digests.view}
        isEmpty={(rows) => rows.length === 0}
        loading={<CardGridSkeleton listClass="digests__list" />}
        empty={<EmptyState message={t('digests.empty')} />}
      >
        {(rows) => (
          <Panel
            title={t('digests.heading')}
            footer={`${t('digests.next_title')} — ${t('digests.next_note')}`}
          >
            <ul class="row-list digests__list">
              <For each={rows}>
                {(row) => (
                  <DigestCard
                    row={row}
                    active={selected()?.id === row.id}
                    onOpen={(d) => setSelected(d)}
                  />
                )}
              </For>
            </ul>
            <LoadMore list={digests} />
          </Panel>
        )}
      </QueryBoundary>

      <DigestDrawer digest={selected()} token={token} onClose={() => setSelected(null)} />
    </section>
  );
}
